import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

const GAP = 8;
const Z_INDEX = 9999;
/** Debe coincidir con SCSS: $arrow-offset + $arrow-size (base del triángulo desde el borde). */
const ARROW_BASE_OFFSET = 4 + 5;
/** Centro del triángulo de la flecha respecto al borde (start/end): base + mitad del tamaño. */
const ARROW_ANCHOR_OFFSET = ARROW_BASE_OFFSET + 5;

type Position = "top" | "bottom" | "left" | "right";
type Align = "start" | "center" | "end";

export class CoreTooltipPlugin implements Plugin {
  private tooltip: HTMLElement | null = null;
  private boundShow: () => void;
  private boundHide: () => void;
  private boundScheduleHide: () => void;
  private boundCancelHide: () => void;

  constructor(private readonly trigger: HTMLElement) {
    this.boundShow = this.show.bind(this);
    this.boundHide = this.hide.bind(this);
    this.boundScheduleHide = this.scheduleHide.bind(this);
    this.boundCancelHide = this.cancelHide.bind(this);
  }

  init(): void {
    const tooltipId = this.trigger.dataset.tooltipId ?? "";
    if (!tooltipId) {
      console.warn("CoreTooltip: trigger must have data-tooltip-id");
      return;
    }

    this.tooltip = document.getElementById(tooltipId);
    if (!this.tooltip) {
      console.warn(`CoreTooltip: tooltip with id "${tooltipId}" not found`);
      return;
    }

    const position = (this.trigger.dataset.tooltipPosition ||
      "bottom") as Position;
    const align = (this.trigger.dataset.tooltipAlign || "center") as Align;
    this.applyArrowClasses(position, align);
    this.tooltip.setAttribute("aria-hidden", "true");
    this.tooltip.style.position = "fixed";
    this.tooltip.style.zIndex = String(Z_INDEX);
    this.tooltip.style.pointerEvents = "none";
    this.tooltip.style.visibility = "hidden";
    this.tooltip.style.opacity = "0";

    this.trigger.addEventListener("mouseenter", this.boundShow);
    this.trigger.addEventListener("mouseleave", this.boundScheduleHide);
    this.trigger.addEventListener("focus", this.boundShow);
    this.trigger.addEventListener("blur", this.boundScheduleHide);

    this.tooltip.addEventListener("mouseenter", this.boundCancelHide);
    this.tooltip.addEventListener("mouseleave", this.boundScheduleHide);
  }

  /** Posición del tooltip respecto al trigger → borde del tooltip donde va la flecha (apuntando al trigger). */
  private static readonly POSITION_TO_ARROW: Record<Position, Position> = {
    bottom: "top",
    top: "bottom",
    left: "right",
    right: "left",
  };

  private applyArrowClasses(position: Position, align: Align): void {
    if (!this.tooltip) return;

    const validPositions: Position[] = ["top", "bottom", "left", "right"];
    const validAligns: Align[] = ["start", "center", "end"];
    const pos = validPositions.includes(position) ? position : "bottom";
    const arrowPos = CoreTooltipPlugin.POSITION_TO_ARROW[pos];
    const al = validAligns.includes(align) ? align : "center";

    this.tooltip.classList.remove(
      "core-tooltip--arrow-top",
      "core-tooltip--arrow-bottom",
      "core-tooltip--arrow-left",
      "core-tooltip--arrow-right",
      "core-tooltip--arrow-align-start",
      "core-tooltip--arrow-align-center",
      "core-tooltip--arrow-align-end",
    );
    this.tooltip.classList.add(
      `core-tooltip--arrow-${arrowPos}`,
      `core-tooltip--arrow-align-${al}`,
    );
  }

  private scheduleHide(): void {
    this.hide();
  }

  private cancelHide(): void {
    // Reservado por si se añade de nuevo un pequeño delay de "hover intent"
  }

  private show(): void {
    if (!this.tooltip) return;
    this.positionTooltip();
    this.tooltip.setAttribute("aria-hidden", "false");
    this.tooltip.style.visibility = "visible";
    this.tooltip.style.opacity = "1";
  }

  private hide(): void {
    if (!this.tooltip) return;
    this.tooltip.setAttribute("aria-hidden", "true");
    this.tooltip.style.visibility = "hidden";
    this.tooltip.style.opacity = "0";
  }

  private positionTooltip(): void {
    if (!this.tooltip) return;

    const position = (this.trigger.dataset.tooltipPosition ||
      "bottom") as Position;
    const align = (this.trigger.dataset.tooltipAlign || "center") as Align;
    const validPositions: Position[] = ["top", "bottom", "left", "right"];
    const validAligns: Align[] = ["start", "center", "end"];
    const pos = validPositions.includes(position) ? position : "bottom";
    const al = validAligns.includes(align) ? align : "center";

    const triggerRect = this.trigger.getBoundingClientRect();
    const tooltipRect = this.tooltip.getBoundingClientRect();
    const triggerCenterX = triggerRect.left + triggerRect.width / 2;
    const triggerCenterY = triggerRect.top + triggerRect.height / 2;
    const viewportWidth = document.documentElement.clientWidth;

    let effectivePosition: Position = pos;
    let effectiveAlign: Align = al;

    if (pos === "left" || pos === "right") {
      const fitsLeft = triggerRect.left - tooltipRect.width - GAP >= 0;
      const fitsRight =
        triggerRect.right + GAP + tooltipRect.width <= viewportWidth;
      // Si no cabe en ningún lado (p. ej. mobile), pasa debajo del trigger
      if (pos === "left" && !fitsLeft)
        effectivePosition = fitsRight ? "right" : "bottom";
      else if (pos === "right" && !fitsRight)
        effectivePosition = fitsLeft ? "left" : "bottom";
      if (effectivePosition === "bottom") effectiveAlign = "center";
    }

    if (effectivePosition === "top" || effectivePosition === "bottom") {
      effectiveAlign = this.fitHorizontalAlign(
        triggerCenterX,
        tooltipRect.width,
        effectiveAlign,
        viewportWidth,
      );
    }

    this.applyArrowClasses(effectivePosition, effectiveAlign);

    let top = 0;
    let left = 0;

    switch (effectivePosition) {
      case "top": {
        top = triggerRect.top - tooltipRect.height - GAP;
        left = this.alignHorizontal(
          triggerCenterX,
          tooltipRect.width,
          effectiveAlign,
        );
        break;
      }
      case "bottom": {
        top = triggerRect.bottom + GAP;
        left = this.alignHorizontal(
          triggerCenterX,
          tooltipRect.width,
          effectiveAlign,
        );
        break;
      }
      case "left": {
        top = this.alignVertical(
          triggerCenterY,
          tooltipRect.height,
          effectiveAlign,
        );
        left = triggerRect.left - tooltipRect.width - GAP;
        break;
      }
      case "right": {
        top = this.alignVertical(
          triggerCenterY,
          tooltipRect.height,
          effectiveAlign,
        );
        left = triggerRect.right + GAP;
        break;
      }
      default: {
        top = triggerRect.bottom + GAP;
        left = this.alignHorizontal(
          triggerCenterX,
          tooltipRect.width,
          effectiveAlign,
        );
      }
    }

    this.tooltip.style.top = `${top}px`;
    this.tooltip.style.left = `${left}px`;
  }

  /** Devuelve la primera alineación (preferida, center, start, end) con la que el tooltip cabe en el viewport. */
  private fitHorizontalAlign(
    centerX: number,
    tooltipWidth: number,
    preferred: Align,
    viewportWidth: number,
  ): Align {
    const candidates: Align[] = [preferred, "center", "start", "end"];
    const fits = (align: Align) => {
      const left = this.alignHorizontal(centerX, tooltipWidth, align);
      return left >= 0 && left + tooltipWidth <= viewportWidth;
    };
    return candidates.find(fits) ?? preferred;
  }

  /** Calcula left del tooltip para que el ancla de la flecha (según align) coincida con centerX. */
  private alignHorizontal(
    centerX: number,
    tooltipWidth: number,
    align: Align,
  ): number {
    switch (align) {
      case "start":
        return centerX - ARROW_ANCHOR_OFFSET;
      case "end":
        return centerX - tooltipWidth + ARROW_ANCHOR_OFFSET;
      default:
        return centerX - tooltipWidth / 2;
    }
  }

  /** Calcula top del tooltip para que el ancla de la flecha (según align) coincida con centerY. */
  private alignVertical(
    centerY: number,
    tooltipHeight: number,
    align: Align,
  ): number {
    switch (align) {
      case "start":
        return centerY - ARROW_ANCHOR_OFFSET;
      case "end":
        return centerY - tooltipHeight + ARROW_ANCHOR_OFFSET;
      default:
        return centerY - tooltipHeight / 2;
    }
  }

  destroy(): void {
    this.trigger.removeEventListener("mouseenter", this.boundShow);
    this.trigger.removeEventListener("mouseleave", this.boundScheduleHide);
    this.trigger.removeEventListener("focus", this.boundShow);
    this.trigger.removeEventListener("blur", this.boundScheduleHide);
    if (this.tooltip) {
      this.tooltip.removeEventListener("mouseenter", this.boundCancelHide);
      this.tooltip.removeEventListener("mouseleave", this.boundScheduleHide);
      this.tooltip.style.visibility = "";
      this.tooltip.style.opacity = "";
      this.tooltip.style.top = "";
      this.tooltip.style.left = "";
    }
  }
}

PluginRegistry.register("core-tooltip", CoreTooltipPlugin);
